import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { useHealth } from '../context/HealthContext';
import { getSiteSettings } from '../utils/getSiteSettings';
import { detectCurrency } from '../utils/geoDetect';
import { usePageMeta } from '../hooks/usePageMeta';
import { useJsonLd } from '../hooks/useJsonLd';
import apiService from '../services/apiService';
import * as pdfjsLib from 'pdfjs-dist';
import BetaWelcomeModal from '../components/BetaWelcomeModal';
import HelpfulStatBadge from '../components/HelpfulStatBadge';
import { renderRichText } from '../utils/boldText';

// Set up PDF.js worker
pdfjsLib.GlobalWorkerOptions.workerSrc = `//cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjsLib.version}/pdf.worker.min.js`;

function renderMarkdown(text) {
  if (!text) return null;
  const parts = [];
  let key = 0;
  const regex = /(\*\*(.+?)\*\*|\*(.+?)\*)/g;
  let lastIndex = 0;
  let match;
  while ((match = regex.exec(text)) !== null) {
    if (match.index > lastIndex) {
      parts.push(text.slice(lastIndex, match.index));
    }
    if (match[2]) {
      parts.push(<strong key={key++}>{match[2]}</strong>);
    } else if (match[3]) {
      parts.push(<em key={key++}>{match[3]}</em>);
    }
    lastIndex = regex.lastIndex;
  }
  if (lastIndex < text.length) {
    parts.push(text.slice(lastIndex));
  }
  return parts;
}

function VisionStrip({ title, body }) {
  const [expanded, setExpanded] = React.useState(false);
  if (!title && !body) return null;
  const paragraphs = body ? body.split('\n\n') : [];
  const firstParagraph = paragraphs[0] || '';
  const restParagraphs = paragraphs.slice(1);
  const hasMore = restParagraphs.length > 0;

  return (
    <div
      className="max-w-2xl mx-auto mt-10 bg-blue-50/60 border border-blue-100 rounded-xl px-6 py-5 text-center"
      data-testid="vision-strip"
    >
      {title && (
        <p className="text-base sm:text-lg font-semibold text-slate-700 leading-snug mb-2">
          {title}
        </p>
      )}
      {firstParagraph && (
        <p className="text-sm text-slate-500 leading-relaxed">{firstParagraph}</p>
      )}
      {hasMore && (
        <>
          {expanded && (
            <div className="mt-3 space-y-3">
              {restParagraphs.map((p, i) => (
                <p key={i} className="text-sm text-slate-500 leading-relaxed">{p}</p>
              ))}
            </div>
          )}
          <button
            onClick={() => setExpanded(!expanded)}
            className="mt-3 text-xs text-blue-500 hover:text-blue-700 font-medium transition"
            data-testid="vision-strip-read-more"
          >
            {expanded ? 'Show less' : 'Read more'}
          </button>
        </>
      )}
    </div>
  );
}

function HowItWorksAccordion({ triggerText, content }) {
  const [open, setOpen] = React.useState(false);
  if (!triggerText) return null;
  return (
    <div className="max-w-2xl mx-auto mt-6 mb-8" data-testid="how-it-works-accordion">
      <button
        type="button"
        onClick={() => setOpen(o => !o)}
        aria-expanded={open}
        className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-white/60 hover:bg-white border border-[#E8E2D9] rounded-xl text-sm font-medium text-[#2D2825] transition-all hover:border-[#D4C9B8]"
        data-testid="how-it-works-trigger"
      >
        <span>{triggerText}</span>
        <svg
          className={`w-4 h-4 transition-transform duration-200 ${open ? 'rotate-180' : ''}`}
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>
      <div
        className={`overflow-hidden transition-all duration-300 ease-out ${
          open ? 'max-h-[800px] opacity-100 mt-2' : 'max-h-0 opacity-0'
        }`}
        aria-hidden={!open}
      >
        <div className="bg-white border border-[#E8E2D9] rounded-xl px-6 py-5 text-left text-sm text-[#2D2825] leading-relaxed" data-testid="how-it-works-body">
          {renderRichText(content || '', 'mb-3 last:mb-0')}
        </div>
      </div>
    </div>
  );
}

export default function LandingPage() {
  const navigate = useNavigate();
  const { user } = useHealth();
  /* 
    Settings loaded in 2 phases for smooth UX:
    1. Instant: localStorage cache (avoids flash of defaults)
    2. Background: API fetch (ensures latest Admin edits)
    Fixed Jan 26, 2026: Eliminated "flash of default content" on load
  */
  const [settings, setSettings] = useState(() => {
    // Phase 1: Try localStorage cache first for instant display
    const cached = localStorage.getItem('site_settings');
    if (cached) {
      try {
        return { ...getSiteSettings(), ...JSON.parse(cached) };
      } catch (e) {
        // Invalid cache, use defaults
      }
    }
    return getSiteSettings();
  });
  const [settingsReady, setSettingsReady] = useState(false);
  const fileInputRef = useRef(null);

  usePageMeta(
    'AI Doctor Ben - Private AI Lab Result Timeline & Trends',
    'Privacy-first AI health companion. Keep lab results on your device, track trends over time, and compare 12+ medical perspectives for better doctor visits.',
    'homepage',
    'https://the.aidoctorben.com/'
  );
  const [healthQuestion, setHealthQuestion] = useState('');
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const [isDragOver, setIsDragOver] = useState(false);
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [analysisComplete, setAnalysisComplete] = useState(false);
  const [analysisPreview, setAnalysisPreview] = useState(null);
  const [showPaywall, setShowPaywall] = useState(false);

  // Question placeholder rotation (Apr 28, 2026)
  // Admin sets one phrase per line in `landingQuestionPlaceholderRotation`.
  // 0 or 1 lines → static `landingQuestionPlaceholder` is used.
  // 2+ lines → cycles 3s visible / 1s gap.
  const [rotatedPlaceholder, setRotatedPlaceholder] = useState('');
  useEffect(() => {
    const raw = settings.landingQuestionPlaceholderRotation || '';
    const phrases = raw.split('\n').map(s => s.trim()).filter(Boolean);
    if (phrases.length < 2) {
      setRotatedPlaceholder('');
      return;
    }
    let idx = 0;
    setRotatedPlaceholder(phrases[0]);
    const interval = setInterval(() => {
      // 1s blank gap
      setRotatedPlaceholder('');
      setTimeout(() => {
        idx = (idx + 1) % phrases.length;
        setRotatedPlaceholder(phrases[idx]);
      }, 1000);
    }, 4000); // 3s visible + 1s gap
    return () => clearInterval(interval);
  }, [settings.landingQuestionPlaceholderRotation]);
  
  // Beta Welcome Modal state (Feb 4, 2026)
  const [showBetaWelcome, setShowBetaWelcome] = useState(() => {
    // Only show if enabled in admin settings AND once per session
    const hasSeenBetaWelcome = sessionStorage.getItem('beta_welcome_seen');
    if (hasSeenBetaWelcome) return false;
    const siteSettings = getSiteSettings();
    return siteSettings.betaWelcomeEnabled !== false;
  });
  
  // Beta code entry in paywall modal
  const [betaCode, setBetaCode] = useState('');
  const [betaCodeError, setBetaCodeError] = useState('');
  const [betaCodeLoading, setBetaCodeLoading] = useState(false);
  const [showBetaCodeEntry, setShowBetaCodeEntry] = useState(false);  // Shows expanded beta code form
  
  // Waitlist state (Feb 6, 2026)
  const [waitlistEmail, setWaitlistEmail] = useState('');
  const [waitlistLoading, setWaitlistLoading] = useState(false);
  const [waitlistResult, setWaitlistResult] = useState(null); // { success, position, referral_code, referral_link, error, show_resend }
  const [waitlistError, setWaitlistError] = useState('');
  const [showWaitlistModal, setShowWaitlistModal] = useState(false); // Controls public waitlist modal
  
  // Swipe gesture state for paywall modal
  const [touchStart, setTouchStart] = useState(null);
  const [touchEnd, setTouchEnd] = useState(null);
  const minSwipeDistance = 80; // Minimum swipe distance to trigger close

  // Geo-region for price-sensitive copy (Feb 27, 2026)
  const [region, setRegion] = useState('US'); // default US until detected
  const [detectedCurrency, setDetectedCurrency] = useState('USD');
  useEffect(() => {
    detectCurrency().then(({ currency }) => {
      setDetectedCurrency(currency);
      if (currency === 'GBP') setRegion('UK');
      else if (currency === 'EUR') setRegion('EU');
      else if (currency === 'CZK') setRegion('CZ');
      else if (currency === 'JPY') setRegion('JP');
      else if (currency === 'DKK') setRegion('DK');
      else if (currency === 'KZT') setRegion('KZ');
      else setRegion('US');
    });
  }, []);

  // Currency display helpers for pricing cards
  const currencySymbols = { USD: '$', GBP: '£', EUR: '€', CZK: 'Kč', JPY: '¥', DKK: 'kr', KZT: '₸' };
  const CURRENCY_PRICES = {
    personal: { USD: 16, GBP: 13, EUR: 12, CZK: 200, JPY: 1500, DKK: 69, KZT: 3350 },
    family: { USD: 60, GBP: 45, EUR: 52 }
  };
  const getLocalPrice = (plan) => {
    const prices = CURRENCY_PRICES[plan];
    if (!prices) return null;
    const price = prices[detectedCurrency] ?? prices['USD'];
    const curr = prices[detectedCurrency] ? detectedCurrency : 'USD';
    const sym = currencySymbols[curr] || '$';
    return { price, sym, curr };
  };
  
  const handleTouchStart = (e) => {
    if (e.targetTouches && e.targetTouches[0]) {
      setTouchEnd(null);
      setTouchStart(e.targetTouches[0].clientX);
    }
  };
  
  const handleTouchMove = (e) => {
    if (e.targetTouches && e.targetTouches[0]) {
      setTouchEnd(e.targetTouches[0].clientX);
    }
  };
  
  const handleTouchEnd = () => {
    if (!touchStart || !touchEnd) return;
    const distance = touchEnd - touchStart;
    const isRightSwipe = distance > minSwipeDistance;
    if (isRightSwipe) {
      // Close paywall and reset state
      closePaywall();
    }
  };
  
  const closePaywall = () => {
    setShowPaywall(false);
    setAnalysisPreview(null);
    setBetaCode('');
    setBetaCodeError('');
    setShowBetaCodeEntry(false);
  };
  
  // ============================================================
  // HOME PAGE FAQ - Database-driven with code fallbacks
  // Last updated: February 5, 2026
  // 
  // ARCHITECTURE:
  // 1. Code defaults = fallback (if API fails or DB empty)
  // 2. Database content = primary source (Admin panel changes show here)
  // 3. API fetched on page load to get latest Admin edits
  // ============================================================
  const FAQ_DEFAULTS = {
    section_title: "❓ Quick Answers",
    q1: {
      question: "Q: I am worried about my husbands cholesterol.",
      answer: "A: Upload his labs to your family records → both of you chat with me → have your husband see his results on Timeline charts → discuss our 5+ health perspectives"
    },
    q2: {
      question: "Q: Is my health data private?",
      answer: "A: Your health records are stored in a private vault on your device using browser-based storage - we don't keep a copy of them on our servers. When you chat with AI Doctor Ben and choose to attach records, a redacted text snapshot (your question and the relevant lab values or notes) is sent securely to our AI providers to generate a response, then discarded - we don't keep or build a database of your records there. We are also actively building a version where the AI can run fully on your device, with the goal of reaching 100% on‑device privacy so nothing needs to leave your phone at all in future."
    },
    q3: {
      question: "Q: Is AI Doctor Ben medical advice?",
      answer: "A: We are not a doctor or a diagnosis service. We are here to sit beside you, help you make sense of your blood tests and health notes. We give you clearer insightful language you can always use for your next appointment. Think of us as a calm, organised second brain for your health – not a replacement for your GP or specialist. Always talk to a qualified clinician before making decisions about tests, treatment, or medication."
    }
  };

  // State starts with defaults, then updates from database
  const [faqConfig, setFaqConfig] = useState({
    home_section_title: FAQ_DEFAULTS.section_title,
    quick_q1_question: FAQ_DEFAULTS.q1.question,
    quick_q1_answer: FAQ_DEFAULTS.q1.answer,
    quick_q2_question: FAQ_DEFAULTS.q2.question,
    quick_q2_answer: FAQ_DEFAULTS.q2.answer,
    quick_q3_question: FAQ_DEFAULTS.q3.question,
    quick_q3_answer: FAQ_DEFAULTS.q3.answer
  });

  // Homepage FAQ schema (3 visible Q&A) — kept distinct from /faq's 20+ entries
  // and from the canonical Organization/SoftwareApplication schemas in
  // public/index.html. Per CODING_PROTOCOLS §31: JSON-LD must be injected via
  // useJsonLd, never inline <script> tags in JSX.
  // Removed May 5, 2026: redundant Organization stub + WebApplication overlap
  // (the Organization in index.html is far richer; SoftwareApplication already
  // covers the WebApplication entity).
  useJsonLd('homepage-faq', {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": [
      {
        "@type": "Question",
        "name": faqConfig.quick_q1_question,
        "acceptedAnswer": { "@type": "Answer", "text": faqConfig.quick_q1_answer }
      },
      {
        "@type": "Question",
        "name": faqConfig.quick_q2_question,
        "acceptedAnswer": { "@type": "Answer", "text": faqConfig.quick_q2_answer }
      },
      {
        "@type": "Question",
        "name": faqConfig.quick_q3_question,
        "acceptedAnswer": { "@type": "Answer", "text": faqConfig.quick_q3_answer }
      }
    ]
  });

  const handleFileUpload = (files) => {
    const validFiles = Array.from(files).filter(file => {
      const validTypes = ['application/pdf', 'image/jpeg', 'image/png', 'image/jpg'];
      return validTypes.includes(file.type) && file.size <= 10 * 1024 * 1024; // 10MB max
    });
    
    if (uploadedFiles.length + validFiles.length > 10) {
      alert('Maximum 10 files allowed');
      return;
    }
    
    setUploadedFiles(prev => [...prev, ...validFiles.slice(0, 10 - prev.length)]);
  };

  useEffect(() => {
    /*
      Phase 2: Background API fetch for latest Admin edits
      - Silently updates settings if different from cache
      - Updates localStorage cache for next visit
      - No visual flash because cache showed correct content instantly
    */
    const loadSettings = async () => {
      try {
        const result = await apiService.getGeneralSettings();
        if (result && result.settings) {
          const merged = { ...settings, ...result.settings };
          setSettings(merged);
          // Update cache for next visit
          localStorage.setItem('site_settings', JSON.stringify(merged));
        }
      } catch (e) {
        console.log('Using cached/default settings for landing page');
      } finally {
        setSettingsReady(true);
      }
    };
    loadSettings();
    
    // Load FAQ from database (Admin panel changes)
    // Falls back to FAQ_DEFAULTS if API fails
    const loadFAQConfig = async () => {
      try {
        const result = await apiService.getFAQConfig();
        if (result && result.quick_q1_question) {
          // Database content takes priority over defaults
          setFaqConfig(prev => ({ ...prev, ...result }));
          console.log('[FAQ] Loaded from database');
        }
      } catch (e) {
        console.log('[FAQ] Using defaults (API unavailable)');
      }
    };
    loadFAQConfig();
  }, []);

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragOver(false);
    handleFileUpload(e.dataTransfer.files);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    setIsDragOver(true);
  };

  const handleDragLeave = () => {
    setIsDragOver(false);
  };

  const removeFile = (index) => {
    setUploadedFiles(prev => prev.filter((_, i) => i !== index));
  };

  // Extract text from PDF
  const extractTextFromPDF = async (file) => {
    const arrayBuffer = await file.arrayBuffer();
    const pdf = await pdfjsLib.getDocument({ data: arrayBuffer }).promise;
    let text = '';
    
    for (let i = 1; i <= pdf.numPages; i++) {
      const page = await pdf.getPage(i);
      const content = await page.getTextContent();
      text += content.items.map(item => item.str).join(' ') + '\n';
    }
    
    return text;
  };

  // Main analysis handler - Option E flow
  const handleAnalyze = async () => {
    if (!healthQuestion.trim() && uploadedFiles.length === 0) {
      alert('Please enter a health question or upload documents');
      return;
    }
    
    setIsAnalyzing(true);
    setAnalysisComplete(false);
    
    try {
      let documentText = healthQuestion;
      
      // Extract text from uploaded files
      if (uploadedFiles.length > 0) {
        for (const file of uploadedFiles) {
          if (file.type === 'application/pdf') {
            const pdfText = await extractTextFromPDF(file);
            documentText += '\n\n--- Document: ' + file.name + ' ---\n' + pdfText;
          }
          // For images, we'd need OCR - for now, note that image was uploaded
          if (file.type.startsWith('image/')) {
            documentText += '\n\n[Image uploaded: ' + file.name + ' - OCR processing available after login]';
          }
        }
      }
      
      // Call the guest analysis API - this DOES the analysis
      const result = await apiService.analyzeGuestDocument(documentText);
      
      if (result.success) {
        // Analysis is complete! But we hide the full results
        setAnalysisComplete(true);
        setAnalysisPreview({
          documentType: result.document_type,
          metricsCount: result.metrics?.length || 0,
          findingsCount: result.key_findings?.length || 0,
          recommendationsCount: result.recommendations?.length || 0,
          // Store full results in sessionStorage for after login
          fullResults: result
        });
        
        // Store for retrieval after login/payment
        sessionStorage.setItem('pendingAnalysis', JSON.stringify({
          question: healthQuestion,
          results: result,
          timestamp: new Date().toISOString()
        }));
        
        // Reset beta code state and show paywall
        setBetaCode('');
        setBetaCodeError('');
        setShowBetaCodeEntry(false);
        setShowPaywall(true);
      }
    } catch (error) {
      console.error('Analysis error:', error);
      alert('Analysis failed. Please try again.');
    } finally {
      setIsAnalyzing(false);
    }
  };

  const handleSubscribe = () => {
    navigate('/pricing');
  };

  const handleLogin = () => {
    navigate('/login');
  };

  // Beta code verification from paywall modal
  const handleBetaCodeSubmit = async (e) => {
    e.preventDefault();
    if (!betaCode.trim()) {
      setBetaCodeError('Please enter a beta code');
      return;
    }
    
    setBetaCodeLoading(true);
    setBetaCodeError('');
    
    try {
      const result = await apiService.verifyBetaCode(betaCode.trim());
      if (result.valid) {
        // Store beta access and redirect to pricing (which will now bypass the gate)
        localStorage.setItem('beta_access_verified', 'true');
        localStorage.setItem('beta_code_used', betaCode.trim().toUpperCase());
        navigate('/pricing');
      } else {
        setBetaCodeError(result.message || 'Invalid beta code');
      }
    } catch (err) {
      setBetaCodeError('Failed to verify code. Please try again.');
    } finally {
      setBetaCodeLoading(false);
    }
  };

  // Waitlist join handler (Feb 6, 2026)
  const handleWaitlistSubmit = async (e) => {
    e.preventDefault();
    if (!waitlistEmail.trim()) {
      setWaitlistError('Please enter your email');
      return;
    }
    
    // Basic email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(waitlistEmail)) {
      setWaitlistError('Please enter a valid email address');
      return;
    }
    
    setWaitlistLoading(true);
    setWaitlistError('');
    
    try {
      // Check for referral code in URL
      const urlParams = new URLSearchParams(window.location.search);
      const refCode = urlParams.get('ref');
      
      const result = await apiService.joinWaitlist(waitlistEmail.trim(), refCode);
      setWaitlistResult(result);
      
      if (result.success) {
        // Clear form
        setWaitlistEmail('');
      }
    } catch (err) {
      setWaitlistError('Something went wrong. Please try again.');
    } finally {
      setWaitlistLoading(false);
    }
  };

  // Resend waitlist link handler
  const handleResendWaitlistLink = async () => {
    setWaitlistLoading(true);
    try {
      await apiService.resendWaitlistLink(waitlistEmail.trim());
      setWaitlistError('');
      setWaitlistResult(prev => ({ ...prev, link_resent: true }));
    } catch (err) {
      setWaitlistError('Failed to resend link. Please try again.');
    } finally {
      setWaitlistLoading(false);
    }
  };

  // Copy referral link to clipboard
  const copyReferralLink = async () => {
    if (waitlistResult?.referral_link) {
      try {
        await navigator.clipboard.writeText(waitlistResult.referral_link);
        // Show brief feedback
        const btn = document.getElementById('copy-referral-btn');
        if (btn) {
          btn.textContent = 'Copied!';
          setTimeout(() => {
            btn.textContent = 'Copy Link';
          }, 2000);
        }
      } catch (err) {
        console.error('Failed to copy:', err);
      }
    }
  };

  // Scroll to beta code entry and open it (for dual-choice section)
  const handleOpenBetaCode = () => {
    // Scroll to top where beta modal is
    window.scrollTo({ top: 0, behavior: 'smooth' });
    // Trigger the paywall with beta code entry
    setTimeout(() => {
      setShowPaywall(true);
      setShowBetaCodeEntry(true);
    }, 500);
  };

  // Open public waitlist modal
  const handleOpenWaitlistModal = () => {
    setShowWaitlistModal(true);
    setWaitlistResult(null);
    setWaitlistError('');
    setWaitlistEmail('');
  };

  // Close waitlist modal
  const closeWaitlistModal = () => {
    setShowWaitlistModal(false);
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-blue-50">
      <Helmet>
        <title>AI Doctor Ben - Private AI Lab Result Timeline & Trends</title>
        <meta name="description" content="Privacy-first AI health companion. Keep lab results on your device, track trends over time, and compare 12+ medical perspectives for better doctor visits." />
        <link rel="canonical" href="https://the.aidoctorben.com/" />
        <meta property="og:title" content="AI Doctor Ben - Private AI Lab Result Timeline & Trends" />
        <meta property="og:description" content="Privacy-first multi-AI health companion that keeps your lab results on your device, shows trends over time, and compares medical perspectives." />
        <meta property="og:url" content="https://the.aidoctorben.com/" />
        <meta property="og:type" content="website" />
        <meta property="og:image" content="https://the.aidoctorben.com/og-image.png" />
        <meta property="og:image:alt" content="AI Doctor Ben - Private AI health companion for lab result analysis and trend tracking" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="AI Doctor Ben - Private AI Lab Result Timeline & Trends" />
        <meta name="twitter:description" content="Privacy-first multi-AI health companion. Lab results on your device, trends over time, 12+ medical perspectives." />
        <meta name="twitter:image" content="https://the.aidoctorben.com/og-image.png" />
      </Helmet>

      {/* Paywall Modal - Option E */}
      {showPaywall && analysisPreview && (
        <div 
          className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-50 p-4"
          onTouchStart={handleTouchStart}
          onTouchMove={handleTouchMove}
          onTouchEnd={handleTouchEnd}
        >
          <div className="bg-white rounded-2xl max-w-lg w-full max-h-[90vh] overflow-y-auto shadow-2xl relative">
            {/* Swipe hint indicator - subtle left arrow */}
            <div className="absolute top-1/2 -left-2 -translate-y-1/2 text-white/60 text-2xl pointer-events-none animate-pulse hidden sm:block">
              ‹
            </div>
            
            {/* Success Header */}
            <div className="bg-gradient-to-r from-green-500 to-emerald-600 p-6 text-center relative sticky top-0 z-10">
              {/* Close button - top right of green header */}
              <button
                onClick={closePaywall}
                className="absolute top-2 right-2 w-10 h-10 flex items-center justify-center rounded-full bg-white/30 hover:bg-white/50 text-white font-bold text-xl transition shadow-md"
                aria-label="Close and go back"
              >
                ✕
              </button>
              <div className="text-5xl mb-2">✅</div>
              <h2 className="text-2xl font-bold text-white">{settings.paywallSuccessTitle || 'Analysis Complete!'}</h2>
              <p className="text-green-100 mt-1">{settings.paywallSuccessSubtitle || 'Your health insights are ready'}</p>
            </div>
            
            {/* Preview Stats - Show what they'll get */}
            <div className="p-6">
              <div className="bg-slate-50 rounded-xl p-4 mb-6">
                <h3 className="font-semibold text-slate-700 mb-3">📊 {settings.paywallPreviewTitle || 'Your Analysis Contains:'}</h3>
                <div className="grid grid-cols-3 gap-4 text-center">
                  <div className="bg-white rounded-lg p-3 shadow-sm">
                    <div className="text-2xl font-bold text-blue-600">{analysisPreview.metricsCount}</div>
                    <div className="text-xs text-slate-500">{settings.paywallMetricsLabel || 'Health Metrics'}</div>
                  </div>
                  <div className="bg-white rounded-lg p-3 shadow-sm">
                    <div className="text-2xl font-bold text-green-600">{analysisPreview.findingsCount}</div>
                    <div className="text-xs text-slate-500">{settings.paywallFindingsLabel || 'Key Findings'}</div>
                  </div>
                  <div className="bg-white rounded-lg p-3 shadow-sm">
                    <div className="text-2xl font-bold text-purple-600">{analysisPreview.recommendationsCount}</div>
                    <div className="text-xs text-slate-500">{settings.paywallRecommendationsLabel || 'Recommendations'}</div>
                  </div>
                </div>
              </div>
              
              {/* Blurred Preview Teaser */}
              <div className="relative mb-6">
                <div className="bg-slate-100 rounded-lg p-4 filter blur-sm select-none">
                  <p className="text-slate-600 text-sm">{settings.paywallBlurText || 'Your cholesterol levels show... HDL at 58 mg/dL which is considered... recommend lifestyle modifications including...'}</p>
                </div>
                <div className="absolute inset-0 flex items-center justify-center">
                  <div className="bg-white/90 backdrop-blur rounded-lg px-4 py-2 shadow-lg">
                    <span className="text-slate-700 font-medium">🔒 {settings.paywallLockText || 'Subscribe to unlock'}</span>
                  </div>
                </div>
              </div>
              
              {/* Compact Pricing Cards */}
              <div className="mb-6">
                <h3 className="font-semibold text-slate-700 mb-3 text-center">💳 Choose Your Plan</h3>
                <div className="grid grid-cols-2 gap-3">
                  {/* Personal Plan */}
                  <div className="border border-slate-200 rounded-xl p-3 hover:border-blue-400 transition cursor-pointer" onClick={() => navigate('/pricing')}>
                    <div className="text-center">
                      <span className="text-2xl">👤</span>
                      <h4 className="font-bold text-slate-800 text-sm">Personal</h4>
                      <p className="text-xl font-bold text-blue-600" data-testid="paywall-personal-price">{getLocalPrice('personal')?.sym}{getLocalPrice('personal')?.price}<span className="text-xs text-slate-400">/mo</span></p>
                      <p className="text-xs text-slate-500">1 user, unlimited records</p>
                    </div>
                  </div>
                  
                  {/* Family Plan - Popular */}
                  <div className="border-2 border-yellow-400 rounded-xl p-3 bg-yellow-50 relative cursor-pointer" onClick={() => navigate('/pricing')}>
                    <div className="absolute -top-2 left-1/2 -translate-x-1/2 bg-yellow-400 text-xs font-bold px-2 py-0.5 rounded text-slate-800">
                      POPULAR
                    </div>
                    <div className="text-center">
                      <span className="text-2xl">👨‍👩‍👧‍👦</span>
                      <h4 className="font-bold text-slate-800 text-sm">Family</h4>
                      <p className="text-xl font-bold text-blue-600" data-testid="paywall-family-price">{getLocalPrice('family')?.sym}{getLocalPrice('family')?.price}<span className="text-xs text-slate-400">/mo</span></p>
                      <p className="text-xs text-slate-500">Up to 4 members</p>
                    </div>
                  </div>
                </div>
                <button 
                  onClick={() => navigate('/pricing')}
                  className="w-full text-center text-blue-600 hover:text-blue-700 text-sm mt-2 font-medium"
                >
                  View all plans & features →
                </button>
              </div>
              
              {/* Primary CTA - Unlock Insights (opens beta code entry) */}
              {!showBetaCodeEntry ? (
                <button
                  onClick={() => setShowBetaCodeEntry(true)}
                  className="w-full bg-gradient-to-r from-emerald-600 to-green-600 hover:from-emerald-700 hover:to-green-700 text-white font-bold py-4 rounded-xl transition shadow-lg mb-4"
                >
                  🔐 Unlock Your Insights →
                </button>
              ) : (
                /* Beta Code Entry - Expanded */
                <div className="mb-4 p-5 bg-gradient-to-r from-emerald-50 to-green-50 rounded-xl border-2 border-emerald-300 shadow-lg">
                  <div className="text-center mb-4">
                    <span className="text-2xl">🔐</span>
                    <p className="text-lg font-bold text-emerald-800 mt-1">Enter Your Beta Code</p>
                    <p className="text-sm text-emerald-600">Unlock full access to your personalized insights</p>
                  </div>
                  <form onSubmit={handleBetaCodeSubmit} className="space-y-3">
                    <input
                      type="text"
                      value={betaCode}
                      onChange={(e) => setBetaCode(e.target.value.toUpperCase())}
                      placeholder="Enter code here"
                      autoFocus
                      className="w-full px-4 py-3 border-2 border-emerald-300 rounded-xl text-center font-mono uppercase text-lg focus:outline-none focus:border-emerald-500 bg-white"
                    />
                    <button
                      type="submit"
                      disabled={betaCodeLoading}
                      className="w-full py-3 bg-emerald-600 hover:bg-emerald-700 text-white font-bold rounded-xl transition disabled:opacity-50"
                    >
                      {betaCodeLoading ? 'Verifying...' : 'Unlock My Insights'}
                    </button>
                  </form>
                  {betaCodeError && (
                    <p className="text-red-500 text-sm text-center mt-3 font-medium">{betaCodeError}</p>
                  )}
                  <p className="text-center text-emerald-600 text-xs mt-4">
                    Don&apos;t have a code? <button onClick={() => navigate('/pricing')} className="text-blue-600 hover:underline font-medium">Request access</button>
                  </p>
                </div>
              )}
              
              {/* Secondary CTAs */}
              <div className="space-y-3">
                {!showBetaCodeEntry && (
                  <div className="text-center text-slate-400 text-xs">— or —</div>
                )}
                
                <button
                  onClick={handleLogin}
                  className="w-full text-slate-500 hover:text-slate-700 font-medium py-2 transition text-sm"
                >
                  {settings.paywallLoginText || 'Already have an account? Login'}
                </button>
              </div>
              
              {/* Privacy Note */}
              <p className="text-center text-slate-400 text-xs mt-4">
                🔒 {settings.paywallPrivacyNote || 'Your analysis is saved locally. It will be available after you subscribe.'}
              </p>
              
              {/* Back to home link */}
              <button
                onClick={closePaywall}
                className="w-full text-center text-slate-400 hover:text-slate-600 text-xs mt-4 py-2 transition"
              >
                ← Back to home
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Header */}
      <header className="bg-white/80 backdrop-blur-sm border-b border-slate-200 sticky top-0 z-40">
        <div className="container mx-auto px-4 py-4 flex justify-between items-center">
          <div className="flex items-center gap-3">
            {settings.logoUrl ? (
              <img src={settings.logoUrl} alt={settings.siteName || 'AI Doctor Ben'} className="h-10" width="40" height="40" />
            ) : (
              <span className="text-3xl">{settings.logo || '🏥🤖'}</span>
            )}
            <div className="flex flex-col">
              <div className="flex items-center gap-2">
                <span className="text-xl font-bold text-slate-800">{settings.siteName || 'AI Doctor Ben'}</span>
                <span className="bg-blue-500 text-white text-xs font-bold px-2 py-0.5 rounded">EARLY ACCESS</span>
              </div>
              {settings.siteTagline && (
                <span className="text-xs text-amber-600 font-semibold italic">
                  &ldquo;{settings.siteTagline}&rdquo;
                </span>
              )}
            </div>
          </div>
          
          <nav className="flex items-center gap-4 md:gap-6">
            <button 
              onClick={() => navigate('/health')}
              className="text-slate-600 hover:text-slate-900 font-medium transition hidden md:block"
            >
              Health
            </button>
            <button 
              onClick={() => navigate('/discover')}
              className="text-slate-600 hover:text-slate-900 font-medium transition hidden md:block"
            >
              Discover
            </button>
            <button 
              onClick={() => navigate('/about')}
              className="text-slate-600 hover:text-slate-900 font-medium transition hidden md:block"
            >
              About
            </button>
            <button 
              onClick={() => navigate('/pricing')}
              className="text-slate-600 hover:text-slate-900 font-medium transition"
            >
              Pricing
            </button>
            {user ? (
              <button
                onClick={() => navigate('/dashboard')}
                className="bg-blue-600 hover:bg-blue-700 text-white px-5 py-2 rounded-lg font-medium transition"
              >
                Dashboard
              </button>
            ) : (
              <button
                onClick={() => navigate('/login')}
                className="bg-blue-600 hover:bg-blue-700 text-white px-5 py-2 rounded-lg font-medium transition"
              >
                Login
              </button>
            )}
          </nav>
        </div>
      </header>

      {/* Main Content */}
      <main className="container mx-auto px-4 py-12">
        {/* Hero Section */}
        <div className="text-center mb-12">
          {settings.landingHeroBadge && (
            <span className="inline-block bg-blue-50 text-blue-600 px-3 py-1 rounded-full text-xs font-semibold mb-4 tracking-wide" data-testid="hero-badge">
              {settings.landingHeroBadge}
            </span>
          )}
          <h1 className={`text-4xl md:text-5xl font-bold text-slate-900 ${
            settings.landingHeroSubheading ? 'mb-4' : 'mb-6'
          }`}>
            {settings.landingHeroTitle || 'See the full story in your blood tests over time – privately, on your device.'}
          </h1>
          {settings.landingHeroSubheading && (
            <p className="text-lg text-slate-500 font-medium mb-4 whitespace-pre-line" data-testid="hero-subheading">
              {renderMarkdown(settings.landingHeroSubheading)}
            </p>
          )}
          {settings.landingHeroSubtitle && (
            <p className="text-xl text-slate-600 max-w-2xl mx-auto whitespace-pre-line" data-testid="hero-subtitle">
              {renderMarkdown(settings.landingHeroSubtitle)}
            </p>
          )}
        </div>

        {/* Inline accordion: "How it works & stays private" — between subtitle and input box.
            Admin toggle: landingHowItWorksEnabled (default 'true' = ON).
            Editable trigger + body via landingHowItWorksTrigger / landingHowItWorksContent.
            Body supports **bold**, *italic*, and \n paragraph breaks. */}
        {(settings.landingHowItWorksEnabled !== 'false' && settings.landingHowItWorksEnabled !== false) &&
          settings.landingHowItWorksTrigger && (
            <HowItWorksAccordion
              triggerText={settings.landingHowItWorksTrigger}
              content={settings.landingHowItWorksContent}
            />
          )
        }

        {/* Privacy & Beta Strip */}
        <div className="max-w-2xl mx-auto text-center mb-10 space-y-2">
          {settings.landingBelowHeroPrivacy && (
            <p className="text-sm text-slate-500" data-testid="below-hero-privacy">
              {settings.landingBelowHeroPrivacy}
            </p>
          )}
          {settings.landingTrustLine && (
            <p className="text-sm font-semibold text-slate-600" data-testid="trust-line">
              {renderMarkdown(settings.landingTrustLine)}
            </p>
          )}
          {settings.landingBetaLineVisible !== 'false' && settings.landingBetaLineVisible !== false && (
          <p className="text-sm font-semibold text-slate-700" data-testid="beta-price-line">
            {({US: settings.landingBelowHeroBetaUS, UK: settings.landingBelowHeroBetaUK, EU: settings.landingBelowHeroBetaEU, CZ: settings.landingBelowHeroBetaCZ, JP: settings.landingBelowHeroBetaJP, DK: settings.landingBelowHeroBetaDK, KZ: settings.landingBelowHeroBetaKZ}[region]) || settings.landingBelowHeroBeta || ({US: '$16/month locks in forever. Rivals charge $99.', UK: '£13/month locks in forever. Rivals charge £99.', EU: '€14/month locks in forever. Rivals charge €99.', CZ: '200 Kč/month locks in forever.', JP: '¥1,500/month locks in forever.', DK: '69 kr/month locks in forever.', KZ: '₸3,350/month locks in forever.'}[region]) || '$16/month locks in forever. Rivals charge $99.'}
          </p>
          )}
        </div>

        {/* Consultation Card */}
        <div className="max-w-3xl mx-auto">
          <div className="bg-white rounded-2xl shadow-xl border border-slate-200 p-8">
            
            {/* Health Question Input - WhatsApp/ChatGPT Style */}
            <div className="mb-6">
              <label className="block text-sm font-semibold text-slate-700 mb-2">
                {settings.landingQuestionLabel || 'Your Health Question'}
              </label>
              <div className="flex items-end gap-2 bg-slate-100 rounded-3xl px-4 py-3 border border-slate-200 focus-within:border-blue-400 focus-within:ring-2 focus-within:ring-blue-100 transition-all">
                <textarea
                  value={healthQuestion}
                  onChange={(e) => setHealthQuestion(e.target.value)}
                  placeholder={
                    rotatedPlaceholder ||
                    settings.landingQuestionPlaceholder ||
                    "Describe your health concern or question... (e.g., 'What do my cholesterol levels mean?' or 'I've been experiencing too much fatigue lately...')"
                  }
                  className="flex-1 bg-transparent text-slate-800 placeholder-slate-400 focus:outline-none resize-none text-base leading-relaxed transition-opacity duration-300"
                  rows={2}
                  disabled={isAnalyzing}
                  style={{ minHeight: '50px', maxHeight: '120px' }}
                  data-testid="health-question-input"
                />
              </div>
            </div>

            {/* Document Upload */}
            <div className="mb-6">
              {settings.landingUploadLabel && (
                <label className="block text-sm font-semibold text-slate-700 mb-2">
                  {settings.landingUploadLabel}
                </label>
              )}
              <div
                onDrop={handleDrop}
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
                onClick={() => !isAnalyzing && fileInputRef.current?.click()}
                className={`border-2 border-dashed rounded-xl p-8 text-center transition ${
                  isAnalyzing ? 'opacity-50 cursor-not-allowed' :
                  isDragOver 
                    ? 'border-blue-500 bg-blue-50 cursor-pointer' 
                    : 'border-slate-300 hover:border-blue-400 hover:bg-slate-50 cursor-pointer'
                }`}
              >
                <input
                  ref={fileInputRef}
                  type="file"
                  multiple
                  accept=".pdf,.jpg,.jpeg,.png"
                  onChange={(e) => handleFileUpload(e.target.files)}
                  className="hidden"
                  disabled={isAnalyzing}
                />
                <div className="text-4xl mb-2">📄</div>
                {settings.landingUploadText && (
                  <p className="text-slate-600 font-medium">
                    {settings.landingUploadText}
                  </p>
                )}
                {settings.landingUploadHint && (
                  <p className="text-slate-400 text-sm mt-1">
                    {settings.landingUploadHint}
                  </p>
                )}
                <div className="flex items-center justify-center gap-4 mt-3" data-testid="trust-badges">
                  <span className="inline-flex items-center gap-1 text-xs text-slate-400">
                    <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" /></svg>
                    Device-isolated storage
                  </span>
                  <span className="inline-flex items-center gap-1 text-xs text-slate-400">
                    <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" /></svg>
                    Records stay on your device
                  </span>
                </div>
              </div>

              {/* Uploaded Files List */}
              {uploadedFiles.length > 0 && (
                <div className="mt-4 space-y-2">
                  {uploadedFiles.map((file, index) => (
                    <div key={index} className="flex items-center justify-between bg-slate-50 rounded-lg px-4 py-2">
                      <div className="flex items-center gap-2">
                        <span className="text-lg">
                          {file.type === 'application/pdf' ? '📄' : '🖼️'}
                        </span>
                        <span className="text-slate-700 text-sm truncate max-w-xs">
                          {file.name}
                        </span>
                        <span className="text-slate-400 text-xs">
                          ({(file.size / 1024 / 1024).toFixed(1)} MB)
                        </span>
                      </div>
                      <button
                        onClick={() => removeFile(index)}
                        disabled={isAnalyzing}
                        className="text-red-500 hover:text-red-700 text-sm font-medium disabled:opacity-50"
                      >
                        Remove
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* EveR LL Governance Trust Mark */}
            {(settings.landingTrustBadgeText || settings.landingTrustBadgeSubtext) && (
              <div className="flex justify-end mb-3" data-testid="ever-ll-badge">
                <span className="inline-flex items-center gap-1.5 text-xs text-slate-500 font-medium tracking-wide">
                  <svg className="w-3.5 h-3.5 text-green-600" fill="currentColor" viewBox="0 0 24 24"><path d="M12 1L3 5v6c0 5.55 3.84 10.74 9 12 5.16-1.26 9-6.45 9-12V5l-9-4zm-1 16l-4-4 1.41-1.41L11 14.17l6.59-6.59L19 9l-8 8z"/></svg>
                  {settings.landingTrustBadgeText && <span>{settings.landingTrustBadgeText}</span>}
                  {settings.landingTrustBadgeText && settings.landingTrustBadgeSubtext && (
                    <span className="text-slate-400">|</span>
                  )}
                  {settings.landingTrustBadgeSubtext && (
                    <span className="text-slate-400">{settings.landingTrustBadgeSubtext}</span>
                  )}
                </span>
              </div>
            )}

            {/* Analyze Button */}
            <button
              onClick={handleAnalyze}
              disabled={isAnalyzing || (!healthQuestion.trim() && uploadedFiles.length === 0)}
              className="w-full bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 text-white font-bold text-lg py-4 rounded-xl transition transform hover:scale-[1.02] shadow-lg disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none"
            >
              {isAnalyzing ? (
                <span className="flex items-center justify-center gap-2">
                  <span className="animate-spin">⏳</span>
                  {settings.landingButtonLoadingText || 'Assessing....'}
                </span>
              ) : (
                <span className="flex items-center justify-center gap-2">
                  {settings.landingButtonText || 'Give me insights'}
                  <span>→</span>
                </span>
              )}
            </button>

            {/* Privacy Line - Below CTA (admin-editable; clear field to hide entirely) */}
            {settings.landingCtaPrivacyLine && (
              <p className="text-center text-slate-500 text-xs mt-3" data-testid="cta-privacy-line">
                {settings.landingCtaPrivacyLine}
              </p>
            )}

            {/* Sub-Button Copy — admin-toggleable, OFF by default. Currently archived. */}
            {(settings.landingSubButtonCopyVisible === 'true' || settings.landingSubButtonCopyVisible === true) && (
              <div className="mt-3" data-testid="sub-button-copy">
                <p className="text-center text-slate-400 text-xs">
                  {settings.landingSubButtonIcon1 || ''} {({US: settings.landingPrivacyNoteUS, UK: settings.landingPrivacyNoteUK, EU: settings.landingPrivacyNoteEU, CZ: settings.landingPrivacyNoteCZ, JP: settings.landingPrivacyNoteJP, DK: settings.landingPrivacyNoteDK, KZ: settings.landingPrivacyNoteKZ}[region]) || settings.landingPrivacyNote || ''}
                </p>
                <p className="text-center text-slate-400 text-xs mt-1">
                  {settings.landingSubButtonIcon2 || '🔒'} {settings.landingPrivacyNote2 || ''}
                </p>
              </div>
            )}

            {/* ARCHIVED 2026-04 — "Have a Beta Code? Get Access" emerald secondary CTA removed.
                Public beta-code entry is no longer surfaced on the homepage. Founder code
                stays available as a quiet, collapsible link on /pricing. */}

          </div>
        </div>

        {/* Vision Strip — Authority/Foresight (admin-editable) */}
        {(settings.visionStripTitle || settings.visionStripBody) && (
          <VisionStrip
            title={settings.visionStripTitle}
            body={settings.visionStripBody}
          />
        )}

        {/* Feature Cards */}
        <h2 className="sr-only">Key Features</h2>
        <div className="grid md:grid-cols-3 gap-6 mt-16 max-w-5xl mx-auto">
          <div className="bg-white rounded-xl p-6 shadow-md border border-slate-100 hover:shadow-lg transition">
            <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center text-2xl mb-4">
              {settings.landingFeature1Icon || '⚡'}
            </div>
            <h3 className="text-lg font-bold text-slate-800 mb-2">{settings.landingFeature1Title || 'Health Insights'}</h3>
            <p className="text-slate-600 text-sm">
              {settings.landingFeature1Desc || 'Get our AI-powered view of your health questions from our panel of AI with multiple points of view. Check out the Timeline feature; Compare and contrast, get insights from your ECG, cholesterol, hormones, supplements and more over months and years.'}
            </p>
          </div>

          <div className="bg-white rounded-xl p-6 shadow-md border border-slate-100 hover:shadow-lg transition">
            <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center text-2xl mb-4">
              {settings.landingFeature2Icon || '🌿'}
            </div>
            <h3 className="text-lg font-bold text-slate-800 mb-2">{settings.landingFeature2Title || 'Health Specialities'}</h3>
            <p className="text-slate-600 text-sm">
              {settings.landingFeature2Desc || 'Conventional medic, integrative, functional, homeopathic, naturopathy, and 7+ other perspectives all in one place.'}
            </p>
          </div>

          <div className="bg-white rounded-xl p-6 shadow-md border border-slate-100 hover:shadow-lg transition">
            <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center text-2xl mb-4">
              {settings.landingFeature3Icon || '📊'}
            </div>
            <h3 className="text-lg font-bold text-slate-800 mb-2">{settings.landingFeature3Title || 'Document Assessment'}</h3>
            <p className="text-slate-600 text-sm">
              {settings.landingFeature3Desc || 'Upload health tracker results, blood tests, lab results, or medical reports, even x-rays. Our multi-AI extracts the data. Records stay on your device - we can\'t see or sell your data (private by design).'}
            </p>
          </div>
        </div>

        {/* Trust Indicators */}
        <div className="text-center mt-16">
          <div className="inline-flex items-center gap-6 text-[#6B635E] text-sm flex-wrap justify-center">
            <span className="flex items-center gap-1">
              <span className="text-green-600">✓</span> Device-isolated storage
            </span>
            <span className="flex items-center gap-1">
              <span className="text-green-600">✓</span> Selected text redaction
            </span>
            <span className="flex items-center gap-1">
              <span className="text-green-600">✓</span> No cloud sync
            </span>
          </div>
        </div>

        {/* Guided Health Questions — small future-direction pilot block (after features, before FAQ) */}
        {(settings.guidedPilotTitle || settings.guidedPilotBody) && (
          <section className="mt-16 max-w-3xl mx-auto" data-testid="guided-health-questions-block">
            <div className="bg-slate-50 border border-slate-200 rounded-2xl p-6 sm:p-8">
              {settings.guidedPilotLabel && (
                <span
                  className="inline-block text-[11px] font-semibold uppercase tracking-wider text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-full px-3 py-1 mb-3"
                  data-testid="guided-pilot-label"
                >
                  {settings.guidedPilotLabel}
                </span>
              )}
              <h2 className="text-lg md:text-xl font-bold text-slate-800 mb-2" data-testid="guided-pilot-title">
                {settings.guidedPilotTitle || 'Guided Health Questions'}
              </h2>
              <p className="text-slate-600 text-sm leading-relaxed mb-3" data-testid="guided-pilot-body">
                {settings.guidedPilotBody || 'We are preparing a small opt-in beta for selected members. The first planned corridor is HbA1c pattern exploration: helping people compare educational paths, uncertainties, trade-offs, and clinician questions in a controlled way.'}
              </p>
              {settings.guidedPilotSmallLine && (
                <p className="text-slate-400 text-xs italic mb-4" data-testid="guided-pilot-smallline">
                  {settings.guidedPilotSmallLine}
                </p>
              )}
              {settings.guidedPilotButtonText && (
                <button
                  onClick={() => navigate(settings.guidedPilotButtonLink || '/about')}
                  className="inline-flex items-center gap-1.5 text-sm font-semibold text-blue-600 hover:text-blue-800 transition"
                  data-testid="guided-pilot-cta"
                >
                  {settings.guidedPilotButtonText} <span aria-hidden="true">→</span>
                </button>
              )}
            </div>
          </section>
        )}

        {/* ARCHIVED 2026-04 — Dual Choice (beta code + public waitlist) section removed.
            Public waitlist is redundant now that the Early Access flow is the default.
            Keeping handlers/modals/state intact below for potential reuse on future projects. */}
      </main>

      {/* Public Waitlist Modal — Schedule A treatment */}
      {showWaitlistModal && (
        <div className="fixed inset-0 bg-black/40 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-3xl p-8 max-w-md w-full border border-[#E8E2D9] shadow-2xl relative">
            {/* Close button */}
            <button
              onClick={closeWaitlistModal}
              className="absolute top-4 right-4 text-[#6B635E] hover:text-[#2D2825] transition"
              data-testid="waitlist-modal-close"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>

            {!waitlistResult?.success && !waitlistResult?.error ? (
              <>
                <div className="text-center mb-6">
                  <h3 className="text-xl font-bold text-[#2D2825] mt-2">
                    Join the Public Waitlist
                  </h3>
                  <p className="text-[#6B635E] text-sm mt-2">
                    No code needed. We'll email you when the next cohort opens.
                  </p>
                </div>
                
                <form onSubmit={handleWaitlistSubmit} className="space-y-4">
                  <input
                    type="email"
                    value={waitlistEmail}
                    onChange={(e) => setWaitlistEmail(e.target.value)}
                    placeholder="Enter your email"
                    autoFocus
                    className="w-full px-4 py-3 bg-[#FBF9F6] border border-[#E8E2D9] rounded-xl text-[#2D2825] placeholder-[#6B635E] focus:outline-none focus:border-[#D0495A] transition"
                    data-testid="waitlist-email-input"
                    disabled={waitlistLoading}
                  />
                  <button
                    type="submit"
                    disabled={waitlistLoading}
                    className="w-full py-3 bg-gradient-to-br from-[#E05A47] to-[#8B3A62] hover:shadow-lg text-white font-bold rounded-full transition disabled:opacity-50 disabled:cursor-not-allowed"
                    data-testid="waitlist-submit-btn"
                  >
                    {waitlistLoading ? 'Joining...' : 'Join Public Waitlist'}
                  </button>
                </form>
                
                {waitlistError && (
                  <p className="text-red-500 text-sm text-center mt-3">{waitlistError}</p>
                )}
                
                <p className="text-[#6B635E] text-xs text-center mt-4">
                  Early members lock in launch pricing when invited.
                </p>
              </>
            ) : waitlistResult?.error === 'already_registered' ? (
              <div className="text-center">
                <p className="text-xl text-[#2D2825] mt-2 mb-2">You're already on the list!</p>
                <p className="text-3xl font-bold text-[#D0495A] mb-4">#{waitlistResult.position}</p>
                
                {waitlistResult.link_resent ? (
                  <p className="text-green-600 text-sm mb-4">Link sent to your email!</p>
                ) : (
                  <button
                    onClick={handleResendWaitlistLink}
                    disabled={waitlistLoading}
                    className="text-[#D0495A] hover:text-[#E05A47] underline text-sm mb-4 disabled:opacity-50"
                  >
                    {waitlistLoading ? 'Sending...' : 'Resend my referral link'}
                  </button>
                )}
                
                <button
                  onClick={closeWaitlistModal}
                  className="w-full mt-4 py-2 text-[#6B635E] hover:text-[#2D2825] transition text-sm"
                >
                  Close
                </button>
              </div>
            ) : waitlistResult?.success ? (
              <div className="text-center" data-testid="waitlist-success">
                <p className="text-xl text-[#2D2825] mt-2 mb-1">You're on the list.</p>
                <p className="text-[#6B635E] text-sm mb-4">
                  Current position: <span className="text-[#D0495A] font-bold">#{waitlistResult.position}</span>
                  <br />
                  <span className="text-xs">(moves up as we invite people and as you share)</span>
                </p>
                
                <div className="border-t border-[#E8E2D9] my-6 pt-6">
                  <p className="text-[#2D2825] text-sm mb-3">Want to move up a little faster?</p>
                  <p className="text-[#6B635E] text-xs mb-4">
                    Each friend who joins nudges you up the list (about 5 spots on average).
                  </p>
                  
                  <div className="flex flex-wrap justify-center gap-2 mb-4">
                    <a
                      href={`https://twitter.com/intent/tweet?text=${encodeURIComponent("I joined the priority list for AI Doctor Ben – helps you understand your blood tests privately on your device.\n\nIf you're into health tracking:")}&url=${encodeURIComponent(waitlistResult.referral_link)}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="px-3 py-2 bg-[#F5F2EC] hover:bg-[#E8E2D9] text-[#2D2825] rounded-lg text-sm transition flex items-center gap-1"
                    >
                      <span>𝕏</span> Share
                    </a>
                    <button
                      id="copy-referral-btn"
                      onClick={copyReferralLink}
                      className="px-3 py-2 bg-[#F5F2EC] hover:bg-[#E8E2D9] text-[#2D2825] rounded-lg text-sm transition"
                    >
                      Copy Link
                    </button>
                    <a
                      href={`mailto:?subject=${encodeURIComponent("Thought you might like this")}&body=${encodeURIComponent(`Hey,\n\nFound something you might be interested in – AI Doctor Ben helps you understand your blood tests and health records, and it keeps everything on your device (privacy-first).\n\nI'm on the priority list: ${waitlistResult.referral_link}`)}`}
                      className="px-3 py-2 bg-[#F5F2EC] hover:bg-[#E8E2D9] text-[#2D2825] rounded-lg text-sm transition"
                    >
                      Email
                    </a>
                  </div>
                  
                  <div className="bg-[#FBF9F6] rounded-lg p-2 text-xs text-[#6B635E] break-all">
                    {waitlistResult.referral_link}
                  </div>
                </div>
                
                <p className="text-xs text-[#6B635E] mt-4">
                  We only track clicks and signups. No social data, ever.
                </p>
                
                <p className="text-[#6B635E] text-xs mt-3">
                  {waitlistResult.total_waiting} people waiting
                </p>
                
                <button
                  onClick={closeWaitlistModal}
                  className="w-full mt-4 py-2 text-[#6B635E] hover:text-[#2D2825] transition text-sm"
                >
                  Close
                </button>
              </div>
            ) : null}
          </div>
        </div>
      )}

      {/* Testimonials — Schedule A warm treatment */}
      <section className="py-16 bg-[#F7F3EE]" data-testid="testimonial-section">
        <div className="container mx-auto px-4 max-w-4xl">
          <h2 className="text-2xl font-bold text-[#2D2825] text-center mb-4">What early users say</h2>
          {/* Aggregated trust badge — May 6, 2026. Hidden until ≥20 member votes accumulate. */}
          <div className="flex justify-center mb-10">
            <HelpfulStatBadge variant="card" />
          </div>
          <div className="grid md:grid-cols-3 gap-6">
            <div className="bg-white rounded-2xl p-6 border border-[#E8E2D9] shadow-sm">
              <span className="text-3xl text-[#D0495A] font-serif leading-none">"</span>
              <p className="text-[#2D2825] text-sm leading-relaxed mt-1">What a brilliant analysis! I love the non-traditional medicine perspectives too. This is fab.</p>
              <div className="mt-4 flex items-center gap-3">
                <div className="w-8 h-8 rounded-full bg-[#D0495A] flex items-center justify-center text-white font-semibold text-xs">MD</div>
                <div>
                  <p className="text-[#2D2825] font-medium text-sm">Mario Di Maggio</p>
                  <p className="text-[#6B635E] text-xs">Bristol, England</p>
                </div>
              </div>
            </div>
            <div className="bg-white rounded-2xl p-6 border border-[#E8E2D9] shadow-sm">
              <span className="text-3xl text-[#D0495A] font-serif leading-none">"</span>
              <p className="text-[#2D2825] text-sm leading-relaxed mt-1">I finally understood what to ask my doctor. The multi-perspective view showed me things I'd never have spotted on my own.</p>
              <div className="mt-4 flex items-center gap-3">
                <div className="w-8 h-8 rounded-full bg-[#8B3A62] flex items-center justify-center text-white font-semibold text-xs">EA</div>
                <div>
                  <p className="text-[#2D2825] font-medium text-sm">Early Access User</p>
                  <p className="text-[#6B635E] text-xs">United Kingdom</p>
                </div>
              </div>
            </div>
            <div className="bg-white rounded-2xl p-6 border border-[#E8E2D9] shadow-sm">
              <span className="text-3xl text-[#D0495A] font-serif leading-none">"</span>
              <p className="text-[#2D2825] text-sm leading-relaxed mt-1">I liked that my family records stayed private on my device. It helped me compare old and new blood test results clearly.</p>
              <div className="mt-4 flex items-center gap-3">
                <div className="w-8 h-8 rounded-full bg-[#E05A47] flex items-center justify-center text-white font-semibold text-xs">EA</div>
                <div>
                  <p className="text-[#2D2825] font-medium text-sm">Early Access User</p>
                  <p className="text-[#6B635E] text-xs">United States</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Quick Answers Section - AEO Optimized (Jan 27, 2026) */}
      <section className="bg-slate-50 py-12 cv-auto">
        <div className="container mx-auto px-4">
          <h2 className="text-2xl font-bold text-center text-slate-800 mb-8">
            {faqConfig.home_section_title}
          </h2>
          
          <div className="max-w-3xl mx-auto space-y-4">
            {/* FAQ Items - H2 for AEO (AI engines scrape H2s for FAQ results) */}
            <div className="bg-white rounded-lg p-4 shadow-sm">
              <h2 className="text-base font-semibold text-slate-800 mb-1">
                {faqConfig.quick_q1_question}
              </h2>
              <p className="text-slate-600 text-sm">
                {faqConfig.quick_q1_answer}
              </p>
            </div>

            <div className="bg-white rounded-lg p-4 shadow-sm">
              <h2 className="text-base font-semibold text-slate-800 mb-1">
                {faqConfig.quick_q2_question}
              </h2>
              <p className="text-slate-600 text-sm">
                {faqConfig.quick_q2_answer}
              </p>
            </div>

            <div className="bg-white rounded-lg p-4 shadow-sm">
              <h2 className="text-base font-semibold text-slate-800 mb-1">
                {faqConfig.quick_q3_question}
              </h2>
              <p className="text-slate-600 text-sm">
                {faqConfig.quick_q3_answer}
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-white border-t border-slate-200 mt-20">
        <div className="container mx-auto px-4 py-8">
          <div className="flex flex-col md:flex-row justify-between items-center gap-4">
            <div className="flex items-center gap-2">
              <span className="text-2xl">{settings.logo || '🏥🤖'}</span>
              <span className="font-semibold text-slate-700">{settings.siteName || 'AI Doctor Ben'}</span>
              <span className="text-slate-400 text-xs">|</span>
              <span className="text-slate-400 text-xs">UK GDPR Compliant</span>
            </div>
            <p className="text-slate-500 text-sm text-center max-w-md">
              {settings.disclaimer || 'Not medical advice. For educational purposes only. Always consult qualified healthcare professionals.'}
            </p>
            <div className="flex gap-4 text-slate-500 text-sm flex-wrap justify-center items-center">
              <button onClick={() => navigate('/health')} className="hover:text-slate-700">Health</button>
              <button onClick={() => navigate('/discover')} className="hover:text-slate-700">Discover</button>
              <button onClick={() => navigate('/about')} className="hover:text-slate-700">About</button>
              <button onClick={() => navigate('/faq')} className="hover:text-slate-700">FAQ</button>
              <button onClick={() => navigate('/pricing')} className="hover:text-slate-700">Pricing</button>
              <button onClick={() => navigate('/privacy')} className="hover:text-slate-700">Privacy</button>
              <button onClick={() => navigate('/terms')} className="hover:text-slate-700">Terms</button>
              <a href="https://x.com/aidoctorben" target="_blank" rel="noopener noreferrer" className="hover:text-slate-700 inline-flex items-center gap-1" data-testid="footer-x-link" title="Follow @aidoctorben on X">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor"><path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/></svg>
              </a>
            </div>
          </div>
        </div>
      </footer>
      
      {/* Beta Welcome Modal (Feb 4, 2026) - Shows once per session for new visitors */}
      {showBetaWelcome && (
        <BetaWelcomeModal
          onClose={() => {
            setShowBetaWelcome(false);
            sessionStorage.setItem('beta_welcome_seen', 'true');
          }}
          onJoinBeta={() => {
            setShowBetaWelcome(false);
            sessionStorage.setItem('beta_welcome_seen', 'true');
            navigate('/pricing'); // Navigate to pricing page with beta signup
          }}
        />
      )}
    </div>
  );
}
